import { SERVER_URL } from "../../../constants.js";

function getHeaders(includeJson = false) {
  const token = sessionStorage.getItem("bearer");

  return {
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
    Authorization: `Bearer ${token}`,
  };
}

async function parseJsonResponse(res, errorMessage) {
  if (res.status === 204 || res.status === 404) {
    return [];
  }
  if (!res.ok) {
    throw new Error(errorMessage);
  }
  const text = await res.text();
  if (!text) return [];
  try {
    return JSON.parse(text);
  } catch {
    return [];
  }
}

// An allocation period is { startDate, endDate } (ISO dates, inclusive): the
// span of class rows the screen is looking at and editing. Today that is an
// academic calendar week; daily allocation is the same period one day long.
// The backend has no notion of a week either — class_lists and the batch save
// take the same two dates — so nothing here should assume one.
export async function fetchClassLists(period, campusName) {
  const { startDate, endDate } = period;
  const params = new URLSearchParams();

  if (campusName) {
    params.append("campusName", campusName);
  }

  const query = params.toString();
  const res = await fetch(
    `${SERVER_URL}api/class_lists/${startDate}/${endDate}${query ? `?${query}` : ""}`,
    {
      headers: getHeaders(),
    }
  );

  return parseJsonResponse(res, "Failed to fetch class lists");
}

export async function fetchClassGroups(campusName, startDate, endDate) {
  const params = new URLSearchParams();

  if (campusName) {
    params.append("campusName", campusName);
  }

  if (startDate) {
    params.append("startDate", startDate);
  }

  if (endDate) {
    params.append("endDate", endDate);
  }

  const query = params.toString();

  const res = await fetch(
    `${SERVER_URL}api/classes/groups${query ? `?${query}` : ""}`,
    {
      headers: getHeaders(),
    }
  );

  return parseJsonResponse(res, "Failed to fetch class groups");
}

export async function fetchActiveCampuses() {
  const res = await fetch(`${SERVER_URL}api/campuses/active`, {
    headers: getHeaders(),
  });

  return parseJsonResponse(res, "Failed to fetch active campuses");
}

export async function fetchAcademicWeeks(campusId, year) {
  const params = new URLSearchParams();
  params.append("type", "ACADEMIC");
  if (campusId) params.append("campusId", campusId);
  if (year) params.append("year", year);

  const res = await fetch(
    `${SERVER_URL}api/calendar-weeks?${params.toString()}`,
    { headers: getHeaders() }
  );

  return parseJsonResponse(res, "Failed to fetch academic weeks");
}

export async function createClass(payload) {
  const res = await fetch(`${SERVER_URL}api/classes`, {
    method: "POST",
    headers: getHeaders(true),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to create class");
  }

  return res.json();
}

export async function fetchClassesForWeek(calendarWeekId) {
  const res = await fetch(
    `${SERVER_URL}api/classes?calendarWeekId=${calendarWeekId}`,
    { headers: getHeaders() }
  );

  return parseJsonResponse(res, "Failed to fetch classes");
}

export async function deleteClass(classId) {
  const res = await fetch(`${SERVER_URL}api/campgroup/delete/${classId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to delete class");
  }
}

export async function scheduleClassForWeek(classId, calendarWeekId) {
  const res = await fetch(`${SERVER_URL}api/classes/${classId}/schedule`, {
    method: "POST",
    headers: getHeaders(true),
    body: JSON.stringify({ calendarWeekId }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to schedule class");
  }
}

export async function descheduleClassFromWeek(classId, calendarWeekId) {
  const res = await fetch(
    `${SERVER_URL}api/classes/${classId}/schedule?calendarWeekId=${calendarWeekId}`,
    {
      method: "DELETE",
      headers: getHeaders(),
    }
  );

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to remove class from week");
  }
}

export async function fetchAvailableClassesForWeek(calendarWeekId) {
  const res = await fetch(
    `${SERVER_URL}api/classes/available?calendarWeekId=${calendarWeekId}`,
    { headers: getHeaders() }
  );

  return parseJsonResponse(res, "Failed to fetch available classes");
}

export async function setClassLeader(classId, adultId, startDate, endDate) {
  const body = { adultId };
  if (startDate) body.startDate = startDate;
  if (endDate) body.endDate = endDate;

  const res = await fetch(`${SERVER_URL}api/campgroup/${classId}/leader`, {
    method: "PUT",
    headers: getHeaders(true),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to update leader");
  }
}

export async function updateClass(classId, payload) {
  const res = await fetch(`${SERVER_URL}api/campgroup/${classId}`, {
    method: "PUT",
    headers: getHeaders(true),
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Failed to update class");
  }
}

export async function fetchAdultsWithStays() {
  const res = await fetch(`${SERVER_URL}api/adultswithstays`, {
    headers: getHeaders(),
  });

  return parseJsonResponse(res, "Failed to fetch adults");
}

export async function fetchWeeklyStaffScheduling(campus, weekStart) {
  const params = new URLSearchParams({ campus, weekStart });
  const res = await fetch(
    `${SERVER_URL}api/staff-scheduling/weekly?${params.toString()}`,
    { headers: getHeaders() }
  );

  return parseJsonResponse(res, "Failed to fetch staff scheduling");
}

// Save a period's class assignments in ONE request and one backend
// transaction — everything lands or nothing does. Each change says where the
// student was (fromClassId, as the period's class_lists load reported it) and
// where they go (toClassId, null to unassign). The backend verifies fromClassId
// against the same class_lists it served us; if someone else moved the student
// since, it answers 409 STALE_CLASS_ASSIGNMENT and nothing is applied. Rows
// outside the period are left alone (a longer row is trimmed or split around
// it), which is what makes editing one day of a week-long row work.
// Resolves to { moved, assigned, unassigned, total }.
export async function saveClassAssignmentChanges(changes, period, campusName) {
  const res = await fetch(`${SERVER_URL}api/classes/assignments`, {
    method: "PUT",
    headers: getHeaders(true),
    body: JSON.stringify({
      campus: campusName || null,
      startDate: period.startDate,
      endDate: period.endDate,
      changes: changes.map(({ studentId, previousGroup, newGroup }) => ({
        studentId,
        fromClassId: previousGroup ?? null,
        toClassId: newGroup ?? null,
      })),
    }),
  });

  if (res.ok) {
    const text = await res.text();
    return text ? JSON.parse(text) : { moved: 0, assigned: 0, unassigned: 0, total: 0 };
  }

  let body = null;
  const text = await res.text().catch(() => "");
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // plain-text body
  }
  const error = new Error(
    (body && body.message) || text || "Failed to save class assignments"
  );
  error.status = res.status;
  error.code = body && body.code ? body.code : undefined;
  error.body = body;
  throw error;
}
